import type { Episode, UserEpisode } from '../types/schemas'
import { useMarkEpisode } from '../hooks/useEpisodes'

interface EpisodeListProps {
  episodes: Episode[]
  markedEpisodes: UserEpisode[]
  nextUnwatchedId?: number
  showId: number
}

export function EpisodeList({
  episodes,
  markedEpisodes,
  nextUnwatchedId,
  showId,
}: EpisodeListProps) {
  const markEpisode = useMarkEpisode()

  const isWatched = (episodeId: number) => {
    return markedEpisodes.some(me => me.episode_id === episodeId && me.type === 0)
  }

  const handleMarkWatched = (episode: Episode) => {
    markEpisode.mutate({
      episodeId: episode.id,
      showId,
      type: 0,
      episodeName: episode.name,
      season: episode.season,
      episode: episode.number,
    })
  }

  // Group episodes by season
  const episodesBySeason = episodes.reduce(
    (acc, episode) => {
      if (!acc[episode.season]) {
        acc[episode.season] = []
      }
      acc[episode.season].push(episode)
      return acc
    },
    {} as Record<number, Episode[]>,
  )

  const seasons = Object.keys(episodesBySeason)
    .map(Number)
    .sort((a, b) => b - a) // Reverse order (newest first)

  return (
    <div className="space-y-6">
      {seasons.map(season => (
        <div key={season} className="bg-slate-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-3">Season {season}</h3>
          <div className="space-y-2">
            {episodesBySeason[season].map(episode => {
              const watched = isWatched(episode.id)
              const isNext = episode.id === nextUnwatchedId

              return (
                <div
                  key={episode.id}
                  className={`flex items-center justify-between p-3 rounded-md transition-colors ${
                    isNext
                      ? 'bg-blue-900/30 border border-blue-700'
                      : watched
                        ? 'bg-slate-700/50'
                        : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                >
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-slate-400 shrink-0">
                        {season}x{String(episode.number).padStart(2, '0')}
                      </span>
                      <h4
                        className={`font-medium truncate ${
                          watched ? 'text-slate-400' : 'text-white'
                        }`}
                      >
                        {episode.name}
                      </h4>
                      {isNext && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded shrink-0">
                          Next
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{episode.airdate || 'TBA'}</p>
                  </div>

                  <button
                    onClick={() => handleMarkWatched(episode)}
                    disabled={watched || markEpisode.isPending}
                    className={`shrink-0 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      watched
                        ? 'bg-green-900/50 text-green-400 cursor-default'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    } ${markEpisode.isPending ? 'opacity-50' : ''}`}
                  >
                    {watched ? 'Watched' : 'Mark Watched'}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
